<template>
  <div>
    <button
      type="button"
      class="print-queue-toggle btn btn-dark rounded-pill d-flex align-items-center gap-2"
      :class="{ 'is-working': workingCount > 0 }"
      @click="toggle"
    >
      <span class="material-icons">print</span>
      <span>Print queue</span>
      <span v-if="workingCount" class="badge rounded-pill bg-warning text-dark">{{ workingCount }}</span>
      <span v-else-if="jobs.length" class="badge rounded-pill bg-secondary">{{ jobs.length }}</span>
    </button>

    <div v-if="open" class="print-queue-backdrop" @click="close"></div>

    <aside class="print-queue-drawer" :class="{ open }" aria-label="Print queue">
      <header class="drawer-header">
        <div>
          <h5 class="mb-0"><strong>Print queue</strong></h5>
          <small class="text-muted">{{ headerSummary }}</small>
        </div>
        <button type="button" class="btn btn-sm btn-link text-muted" @click="close" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </header>

      <div class="drawer-body">
        <p v-if="refreshError" class="queue-refresh-error">{{ refreshError }}</p>
        <p v-if="loading && !jobs.length" class="text-muted small">Loading…</p>
        <p v-else-if="!jobs.length" class="text-muted small">
          Nothing here yet. Frame a sheet on the map and hit print — it will appear here while it renders.
        </p>

        <article v-for="job in jobs" :key="job.id" class="queue-card" :class="`status-${job.status}`">
          <div class="queue-card-main">
            <div class="queue-card-title">{{ job.title || 'Untitled print map' }}</div>
            <div class="queue-card-meta">
              <span class="status-pill" :class="`status-${job.status}`">{{ statusLabel(job) }}</span>
              <span>{{ sheetSummary(job) }}</span>
            </div>
            <div v-if="isWorking(job) && (job.pageCount || 0) > 1" class="progress queue-progress">
              <div class="progress-bar" :style="{ width: `${progressPercent(job)}%` }"></div>
            </div>
            <div v-if="job.errorMessage" class="queue-error">{{ job.errorMessage }}</div>
          </div>

          <div class="queue-card-actions">
            <!-- Gated on the job actually being finished, not merely on a PDF
                 existing: a re-run keeps the previous document attached until
                 the new one replaces it, and offering that mid-render hands
                 the user last time's map. -->
            <a
              v-if="isDownloadable(job)"
              :href="job.pdfUrl || undefined"
              target="_blank"
              rel="noopener"
              class="btn btn-sm btn-primary queue-download"
            >Download</a>
            <button
              type="button"
              class="btn btn-sm queue-remove"
              :disabled="deletingId === job.id"
              :aria-label="`Remove ${job.title || 'print map'}`"
              @click="remove(job)"
            >
              <span class="material-icons">delete_outline</span>
            </button>
          </div>
        </article>
      </div>

      <footer class="drawer-footer">
        <a href="/user/print-maps" class="btn btn-sm btn-outline-secondary w-100">See all print maps</a>
      </footer>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import graphClient from '../../utils/graphql/graph_client';
import { DeletePrintMap, PrintMapQueue } from '../../utils/graphql/print-maps-graph';
import {
  isDownloadable,
  isWorking,
  mergeJobs,
  pollDelay,
  progressPercent,
  queueHeaderSummary,
  sheetSummary,
  shouldRefresh,
  statusLabel,
  workingCount as countWorking,
} from '../../utils/print_maps/printQueueStatus';
import type { QueueJob } from '../../utils/print_maps/printQueueStatus';


const open = ref(false);
const loading = ref(false);
const jobs = ref<QueueJob[]>([]);
const deletingId = ref<string | null>(null);
const refreshError = ref<string | null>(null);
let timer: number | null = null;
// Responses can come back out of order — a slow earlier poll landing after a
// newer one would put the older snapshot on screen and leave a finished job
// showing as still rendering.
let latestRequest = 0;

const workingCount = computed(() => countWorking(jobs.value));

const headerSummary = computed(() => queueHeaderSummary(jobs.value));

async function refresh(): Promise<void> {
  const request = ++latestRequest;
  loading.value = true;

  try {
    const response = await graphClient.request<{ searchUserPrintMaps: { printMaps: QueueJob[] } }>(
      PrintMapQueue,
      { page: 1, perPage: 15 },
    );
    if (request !== latestRequest) return; // a newer poll already answered

    jobs.value = response.searchUserPrintMaps.printMaps || [];
    refreshError.value = null;
  } catch (error) {
    console.error('Failed to load the print queue', error);
    // Surfaced rather than swallowed: a silently failed refresh leaves the
    // last state on screen, which is indistinguishable from a job that has
    // stopped making progress.
    if (request === latestRequest) refreshError.value = 'Could not reach the print queue — retrying.';
  } finally {
    if (request === latestRequest) loading.value = false;
  }
}

async function remove(job: QueueJob): Promise<void> {
  deletingId.value = job.id;
  try {
    await graphClient.request(DeletePrintMap, { id: job.id });
    jobs.value = jobs.value.filter((entry) => entry.id !== job.id);
  } catch (error) {
    console.error('Failed to delete print map', error);
  } finally {
    deletingId.value = null;
  }
}

function scheduleNextPoll(): void {
  if (timer !== null) window.clearTimeout(timer);

  timer = window.setTimeout(async () => {
    // A closed drawer still polls while work is in flight so the badge count
    // stays honest — that is the only signal the user gets that their sheets
    // are on the way.
    if (shouldRefresh(jobs.value, open.value)) await refresh();
    scheduleNextPoll();
  }, pollDelay(jobs.value, open.value));
}

function toggle(): void {
  open.value = !open.value;
  if (open.value) refresh();
  scheduleNextPoll();
}

function close(): void {
  open.value = false;
  scheduleNextPoll();
}

/** Called by the parent right after sheets are queued, so they appear at once. */
function pushJobs(newJobs: QueueJob[]): void {
  jobs.value = mergeJobs(jobs.value, newJobs);
  open.value = true;
  scheduleNextPoll();
}

watch(workingCount, () => scheduleNextPoll());

onMounted(() => {
  refresh();
  scheduleNextPoll();
});

onBeforeUnmount(() => {
  if (timer !== null) window.clearTimeout(timer);
});

defineExpose({ pushJobs, refresh, open });
</script>

<style scoped>
/* The drawer is site furniture, so it wears the site's dark palette rather
   than being a white panel dropped on top of it. */
.print-queue-toggle {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.print-queue-toggle.is-working .material-icons {
  animation: queue-pulse 1.6s ease-in-out infinite;
}

@keyframes queue-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

.print-queue-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1040;
}

.print-queue-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(400px, 92vw);
  background: var(--dark, #17181D);
  color: #fff;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: -4px 0 28px rgba(0, 0, 0, 0.45);
  transform: translateX(100%);
  transition: transform 0.22s ease;
  z-index: 1050;
  display: flex;
  flex-direction: column;
}

.print-queue-drawer.open {
  transform: translateX(0);
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.drawer-header h5 { color: #fff; }
.print-queue-drawer .text-muted { color: var(--text-muted, #B2B2B2) !important; }

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 1rem;
}

.drawer-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.drawer-footer .btn {
  color: var(--text-muted, #B2B2B2);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.drawer-footer .btn:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.06);
}

.queue-card {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  background: var(--gray, #22242A);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  margin-bottom: 0.55rem;
}

.queue-card.status-failed { border-color: rgba(219, 43, 53, 0.45); }
.queue-card.status-completed { border-color: rgba(75, 194, 99, 0.28); }

.queue-card-main { min-width: 0; flex: 1; }

.queue-card-title {
  font-weight: 600;
  font-size: 0.92rem;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  align-items: center;
  font-size: 0.76rem;
  color: var(--text-muted, #B2B2B2);
  margin-top: 0.25rem;
}

.status-pill {
  border-radius: 999px;
  padding: 0.05rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
}

.status-pill.status-processing { background: rgba(253, 223, 0, 0.16); color: var(--yellow, #fddf00); }
.status-pill.status-pending { background: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.7); }
.status-pill.status-completed { background: rgba(75, 194, 99, 0.18); color: #6ed58a; }
.status-pill.status-failed { background: rgba(219, 43, 53, 0.2); color: #ff8a8f; }

.queue-progress {
  height: 4px;
  margin-top: 0.5rem;
  background: rgba(255, 255, 255, 0.12);
}

.queue-progress .progress-bar { background: var(--yellow, #fddf00); }

.queue-error {
  font-size: 0.75rem;
  color: #ff8a8f;
  margin-top: 0.35rem;
}

.queue-refresh-error {
  font-size: 0.78rem;
  color: var(--yellow, #fddf00);
  background: rgba(253, 223, 0, 0.1);
  border: 1px solid rgba(253, 223, 0, 0.25);
  border-radius: 8px;
  padding: 0.4rem 0.55rem;
}

/* Side by side: stacked, a finished job stood three rows tall for two
   buttons. */
.queue-card-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
}

.queue-download { white-space: nowrap; }

.queue-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
  color: var(--text-muted, #B2B2B2);
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
}

.queue-remove:hover {
  color: #ff8a8f;
  border-color: rgba(255, 138, 143, 0.5);
  background: rgba(219, 43, 53, 0.12);
}

.queue-remove .material-icons { font-size: 1.05rem; }
.queue-remove:disabled { opacity: 0.4; }
</style>
